// Moved from `src/hooks/query/useQueryUserPersonalizations.ts` (now the
// delegate) with IP2 (ARCFE-956). This was the 90%-coupled hook of the
// slice; three severances, each an explicit input the delegate supplies:
// - `enabled`: the legacy gate composed the app-resolved Heidi-Learns flag
//   and the desktop online-readiness bridge — both app concerns, so the
//   composed boolean crosses as an input ANDed with the package's own
//   token+user gate (flags stay app-resolved; D5 recipe).
// - `isOfflineNetworkError`: the desktop offline-network predicate is an
//   app/platform util; injected so the frozen cached-fallback-or-rethrow
//   branch runs byte-identically.
// - `onNewInsights`: fired exactly where the legacy queryFn wrote the two
//   app atoms on a total_count increase (ARCFE-921 app-reaction port); the
//   atoms stay app-side with the delegate.
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { GET_USER_PERSONALISATIONS } from '../../contracts/keys';
import { type PersonalizationDataResponse } from '../../contracts/types/user-types';
import { getUserPersonalizations } from '../api/user';
import { getWorkspaceDataConfig } from '../config';
import { useQueryUser } from './useQueryUser';

export { GET_USER_PERSONALISATIONS };

export const getCurrentUserPersonalizations = () =>
  getWorkspaceDataConfig().queryClient.getQueryData<PersonalizationDataResponse>([
    GET_USER_PERSONALISATIONS,
  ]);

export const invalidateCurrentUserPersonalizations = async () =>
  await getWorkspaceDataConfig().queryClient.invalidateQueries({
    queryKey: [GET_USER_PERSONALISATIONS],
  });

interface UseQueryUserPersonalizationsOptions {
  /** App-resolved extra gate (Heidi-Learns flag && desktop online-ready). */
  enabled: boolean;
  /** App/platform predicate for the frozen offline cached-fallback branch. */
  isOfflineNetworkError?: (error: AxiosError) => boolean;
  /** App-reaction port: fired where the legacy queryFn wrote the two atoms. */
  onNewInsights?: () => void;
}

export function useQueryUserPersonalizations({
  enabled,
  isOfflineNetworkError,
  onNewInsights,
}: UseQueryUserPersonalizationsOptions) {
  const token = getWorkspaceDataConfig().useAuthToken();
  const { data: userData } = useQueryUser();

  return useQuery({
    queryKey: [GET_USER_PERSONALISATIONS],
    enabled: !!token && !!userData && enabled,
    queryFn: async () => {
      try {
        const { data } = await getUserPersonalizations();

        const totalPersonalizations = data.total_count;
        const oldData = getCurrentUserPersonalizations();
        if (oldData && oldData.total_count < totalPersonalizations) {
          onNewInsights?.();
        }
        return {
          ...data,
        };
      } catch (error) {
        if (error instanceof AxiosError) {
          if (isOfflineNetworkError?.(error)) {
            const cached = getCurrentUserPersonalizations();
            if (cached) {
              return cached;
            }
            // No cache to fall back on — rethrow so the query enters an error
            // state and refetches on reconnect, rather than caching an empty
            // success that the global staleTime would treat as fresh.
            throw error;
          }

          // !Should handle this logic in query client
          const response = error.response;
          if (!response || !response.status || !response.data) {
            return;
          }
        }
      }
    },
  });
}
